import type { Props, Instance } from 'tippy.js';

type TippyModule = typeof import('tippy.js');
type MultipleTargets = string | Element[] | NodeListOf<Element>;
let tippyModule: TippyModule | null = null;

/**
 * Lazy-load tippy.js and create a tooltip.
 * Use this for tooltips created on-demand (e.g., in event handlers).
 *
 * @example
 * button.addEventListener('click', async () => {
 *    const tooltip = await createTippy(button, { content: 'Hello!' });
 *    tooltip.show();
 * });
 */
export async function createTippy(target: Element, options: Partial<Props>): Promise<Instance> {
   if (!tippyModule) {
      tippyModule = await import('tippy.js');
   }
   return tippyModule.default(target, options) as Instance;
}

/**
 * Lazy-load tippy.js and attach tooltips to multiple elements.
 * Use this for initializing tooltips on a set of elements.
 *
 * @example
 * await createTippyMultiple('.tooltip', { theme: 'custom' });
 * @knipignore
 */
export async function createTippyMultiple(
   targets: MultipleTargets,
   options: Partial<Props>
): Promise<Instance[]> {
   if (!tippyModule) {
      tippyModule = await import('tippy.js');
   }
   return tippyModule.default(targets, options) as Instance[];
}

/**
 * Preload tippy.js without creating any tooltips.
 * Call this on hover/focus to reduce latency when tooltip is needed.
 *
 * @example
 * element.addEventListener('mouseenter', () => preloadTippy(), { once: true });
 * @knipignore
 */
export function preloadTippy(): void {
   if (!tippyModule) {
      void import('tippy.js').then(mod => {
         tippyModule = mod;
      });
   }
}

/**
 * Get the cached tippy module if already loaded.
 * Returns null if tippy hasn't been loaded yet.
 * @knipignore
 */
export function getTippyModule(): TippyModule | null {
   return tippyModule;
}
